import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CreditInOut.css';

const CreditInOut = () => {
  const [bonusData, setBonusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    trade_id: '',
    amount: '',
    action_type: 'add',
    comment: ''
  });

  // Fetch bonus data
  const fetchBonusData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://server.majestyfx.com/admin_fetch_credit_in_out_bonus.php');
      if (response.data.success) {
        setBonusData(response.data.bonus_data || []);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonusData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle add/remove bonus submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      const payload = {
        email: formData.email,
        trade_id: formData.trade_id,
        amount: parseFloat(formData.amount),
        action_type: formData.action_type,
        comment: formData.comment
      };

      const response = await axios.post(
        'https://server.majestyfx.com/admin_add_remove_bonus.php',
        payload
      );

      if (response.data.success) {
        alert(`Bonus ${formData.action_type === 'add' ? 'added' : 'removed'} successfully!`);
        setShowModal(false);
        setFormData({
          email: '',
          trade_id: '',
          amount: '',
          action_type: 'add',
          comment: ''
        });
        fetchBonusData(); // Refresh the data
      } else {
        alert(response.data.message || 'Operation failed');
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Submit error:', err);
    } finally {
      setModalLoading(false);
    }
  };

  // Filter bonus data based on search term
  const filteredData = bonusData.filter(item =>
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.trade_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.comment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="credit-in-out-container">
      <div className="page-header">
        <h1>Credit In/Out (Bonus Management)</h1>
        <p>Manage user bonuses - Add or Remove credits</p>
      </div>

      {/* Action Bar */}
      <div className="action-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by email, trade ID, name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="add-btn" onClick={() => setShowModal(true)}>
          + Add/Remove Bonus
        </button>
      </div>

      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading State */}
      {loading ? (
        <div className="loading">Loading bonus data...</div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="stats-cards">
            <div className="stat-card">
              <h3>Total Records</h3>
              <p>{bonusData.length}</p>
            </div>
            <div className="stat-card add">
              <h3>Total Added</h3>
              <p>{bonusData.filter(b => b.action_type === 'add').length}</p>
            </div>
            <div className="stat-card remove">
              <h3>Total Removed</h3>
              <p>{bonusData.filter(b => b.action_type === 'remove').length}</p>
            </div>
            <div className="stat-card amount">
              <h3>Net Amount</h3>
              <p>
                ${bonusData.reduce((acc, b) => {
                  const amount = parseFloat(b.amount) || 0;
                  return b.action_type === 'add' ? acc + amount : acc - amount;
                }, 0).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Data Table */}
          <div className="table-container">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Account Name</th>
                  <th>Email</th>
                  <th>Trade ID</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Comment</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">No bonus records found</td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr key={item.bonus_id || index}>
                      <td>{item.bonus_id}</td>
                      <td>
                        <div className="account-info">
                          <span className="name">{item.account_name || `${item.first_name} ${item.last_name}`}</span>
                          <span className="type">{item.account_type}</span>
                        </div>
                      </td>
                      <td>{item.email}</td>
                      <td>{item.trade_id}</td>
                      <td className={`amount ${item.action_type}`}>
                        {item.action_type === 'add' ? '+' : '-'}${parseFloat(item.amount).toFixed(2)}
                      </td>
                      <td>
                        <span className={`badge ${item.action_type}`}>
                          {item.action_type === 'add' ? 'Credit In' : 'Credit Out'}
                        </span>
                      </td>
                      <td>{item.comment || '-'}</td>
                      <td>{formatDate(item.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}

      {/* Modal for Add/Remove Bonus */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add/Remove Bonus</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter user email"
                  required
                />
              </div>

              <div className="form-group">
                <label>Trade ID *</label>
                <input
                  type="text"
                  name="trade_id"
                  value={formData.trade_id}
                  onChange={handleInputChange}
                  placeholder="Enter trade ID"
                  required
                />
              </div>

              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Enter amount"
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label>Action Type *</label>
                <div className="action-type-buttons">
                  <button
                    type="button"
                    className={`action-btn add ${formData.action_type === 'add' ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, action_type: 'add' }))}
                  >
                    ➕ Credit In (Add)
                  </button>
                  <button
                    type="button"
                    className={`action-btn remove ${formData.action_type === 'remove' ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, action_type: 'remove' }))}
                  >
                    ➖ Credit Out (Remove)
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Comment</label>
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  placeholder="Enter comment (e.g., Welcome bonus, Adjustment, etc.)"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={modalLoading}>
                  {modalLoading ? 'Processing...' : (formData.action_type === 'add' ? 'Add Bonus' : 'Remove Bonus')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditInOut;
